import { useMutation } from 'react-query';

import client from '@/apis/aniftyBackend/client';
import { useFavoriteUsers } from '@/apis/aniftyBackend/creations/userFavorite/getFavoriteUsers';
import { useIsUserFavorited } from '@/apis/aniftyBackend/creations/userFavorite/getIsUserFavorited';
import useWalletAddress from '@/hooks/useWalletAddress';
import { MutationConfig, queryClient } from '@/utils/react-query';

export const deleteFavoriteUser = ({
  targetUserID,
}: {
  targetUserID: string;
}) => {
  return client.delete(`/api/my_favorite_users/${targetUserID}`);
};

type UseDeleteFavoriteUserOptions = {
  config?: MutationConfig<typeof deleteFavoriteUser>;
};

export const useDeleteFavoriteUser = (
  options?: UseDeleteFavoriteUserOptions
) => {
  const { currentAccountAddress } = useWalletAddress();

  const generateUseUserFavoriteQueryKey = (targetUserID: string) =>
    useIsUserFavorited.getKey(currentAccountAddress, targetUserID);

  return useMutation({
    onMutate: async ({ targetUserID }) => {
      const userFavoriteQueryKey =
        generateUseUserFavoriteQueryKey(targetUserID);

      await queryClient.cancelQueries(userFavoriteQueryKey);

      // Snapshot the previous value
      const previousValue =
        queryClient.getQueryData<boolean>(userFavoriteQueryKey);

      // Optimistically update to state after the mutation
      queryClient.setQueryData<boolean>(userFavoriteQueryKey, false);

      return { previousValue };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, { targetUserID }, context) => {
      queryClient.setQueryData(
        generateUseUserFavoriteQueryKey(targetUserID),
        context.previousValue
      );
    },
    onSettled: async () => {
      // invalidate favorite user list query
      await queryClient.invalidateQueries(
        useFavoriteUsers.getKey(currentAccountAddress)
      );
    },
    ...options,
    mutationFn: deleteFavoriteUser,
  });
};
